import {
  fromCents,
  formatQuantity,
  parseDecimal,
  SALARY_STATE_LABELS,
  type SalaryState,
} from '@k-management/shared';
import { useNavigate } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';

export const peopleKeys = {
  all: ['people'] as const,
  employees: ['people', 'employees'] as const,
  employee: (id: string) => ['people', 'employees', id] as const,
  history: (id: string) => ['people', 'employees', id, 'history'] as const,
  salaries: (id: string, year: number) => ['people', 'employees', id, 'salaries', year] as const,
  tfr: (id: string) => ['people', 'employees', id, 'tfr'] as const,
  month: (year: number, month: number) => ['people', 'month', year, month] as const,
  options: ['people', 'options'] as const,
  payroll: (year: number) => ['people', 'payroll', year] as const,
  cost: (id: string, year: number) => ['people', 'employees', id, 'cost', year] as const,
};

type PeopleView = 'persone' | 'stipendi' | 'costo';

/**
 * Persone, stipendi and company cost are one area (decision 24/10/2026): the
 * three views of Organizzazione › Persone. The cost only with «persone.costs».
 */
export function PeopleViews({ current }: { current: PeopleView }) {
  const navigate = useNavigate();
  const canSeeCosts = useCan('persone.costs');
  const views: [PeopleView, string, string][] = [
    ['persone', 'Persone', '/organizzazione/persone'],
    ['stipendi', 'Stipendi del mese', '/organizzazione/persone/stipendi'],
    ...(canSeeCosts
      ? ([['costo', 'Costo del personale', '/organizzazione/persone/costo']] as [
          PeopleView,
          string,
          string,
        ][])
      : []),
  ];
  return (
    <nav className="subtabs" aria-label="Viste di Persone">
      {views.map(([key, label, path]) => (
        <button
          key={key}
          type="button"
          className={current === key ? 'subtab selected' : 'subtab'}
          onClick={() => void navigate(path)}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

/** «160», «7,5»: hours as the payslips give them. */
export function formatHours(value: string): string {
  return formatQuantity(value);
}

const STATE_PILLS: Record<SalaryState, string> = {
  vuota: 'pill muted',
  acconto: 'pill info',
  'da-pagare': 'pill warn',
  parziale: 'pill warn',
  pagato: 'pill ok',
  eccedente: 'pill bad',
};

export function SalaryStatePill({ state }: { state: SalaryState }) {
  if (state === 'vuota') return null;
  return <span className={STATE_PILLS[state]}>{SALARY_STATE_LABELS[state]}</span>;
}

/** What a money field holds, as the API wants it («1850.00»); null when empty or unreadable. */
export function moneyOf(value: string): string | null {
  if (!value.trim()) return null;
  const number = parseDecimal(value);
  return number === null ? null : fromCents(Math.round(number * 100));
}

/** «IT·· ···· ···· ···· ···· 4521»: the IBAN with only its end in view. */
export function maskedIban(iban: string): string {
  return `IT·· ···· ···· ···· ···· ${iban.slice(-4)}`;
}
